import { FlatButton, Label } from '@globalmed/atlas-ui';
import {
  CSSProperties,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './FormBuilderLabel.module.css';

type TProps = {
  label: string;
  id?: string;
  htmlFor?: string;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  sx?: CSSProperties;
  onLabelChange: (label: string) => void;
};

export const FormBuilderLabel =
  /**
   * Editable label component for form builder fields with auto-resizing textarea
   * @param {TProps} props - Component props
   * @param {string} props.label - Label text
   * @param {string} [props.placeholder] - Placeholder text when label is empty
   * @param {boolean} [props.error=false] - Whether the field has validation errors
   * @param {boolean} [props.disabled=false] - Whether the label editing is disabled
   * @param {boolean} [props.required=false] - Whether the field is required
   * @param {CSSProperties} [props.sx] - Additional styles to apply
   * @param {string} [props.htmlFor] - HTML `for` attribute for the label element.
   * @param {Function} props.onLabelChange - Callback when label changes
   * @returns {JSX.Element} Form builder label component
   */
  memo(
    ({
      label,
      id,
      htmlFor,
      sx,
      placeholder,
      disabled = false,
      error = false,
      required = false,
      onLabelChange,
    }: TProps) => {
      const [isChangeLabel, setIsChangeLabel] = useState(false);
      const textareaRef = useRef<HTMLTextAreaElement>(null);

      const autoResize = useCallback(() => {
        const textarea = textareaRef.current;
        if (textarea) {
          textarea.style.height = 'auto';
          const fontSize = parseInt(
            window.getComputedStyle(textarea).fontSize,
            10,
          );
          const lineHeight = parseInt(
            window.getComputedStyle(textarea).lineHeight,
            10,
          );
          const rowHeight = lineHeight || fontSize;
          const scrollHeight = textarea.scrollHeight;
          const minHeight = rowHeight;

          const maxHeight = 10 * rowHeight;

          textarea.style.height = `${Math.max(
            Math.min(scrollHeight, maxHeight),
            minHeight,
          )}px`;
        }
      }, []);

      useEffect(() => {
        autoResize();
      }, [autoResize, label]);

      useEffect(() => {
        if (!isChangeLabel) return;
        const el = textareaRef.current;
        el?.focus();
        el?.setSelectionRange(el.value.length, el.value.length);
      }, [isChangeLabel]);
      return isChangeLabel ? (
        <textarea
          id={id}
          name="label"
          ref={textareaRef}
          placeholder={placeholder}
          value={label}
          onChange={(e) => onLabelChange(e.target.value)}
          className={styles.textarea}
          rows={1}
          onBlur={() => setIsChangeLabel(false)}
          style={{ ...sx }}
        />
      ) : (
        <FlatButton
          aria-label={label || placeholder}
          type="button"
          onClick={() => setIsChangeLabel(true)}
          disabled={disabled}
        >
          <Label
            id={id}
            sx={{
              ...sx,
              color: error
                ? 'var(--colors-error700)'
                : sx?.color ||
                  `var(${label ? '--colors-gray950' : '--colors-gray500'})`,
            }}
            label={label || placeholder}
            htmlFor={htmlFor}
            required={required}
          />
        </FlatButton>
      );
    },
  );

FormBuilderLabel.displayName = 'FormBuilderLabel';
